<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'

import { Card } from '~ds/components/ui/card'
import { Icon } from '~ds/components/ui/icon'
import { Heading, Text } from '~ds/components/ui/typography'
import { ICONS } from '~ds/lib/icons'
import { cn } from '~ds/lib/utils'
import { Primitive } from 'reka-ui'

import type { ContentCardVariants } from '.'

import { contentCardMediaVariants, contentCardVariants } from '.'

const props = withDefaults(defineProps<{
  as?: PrimitiveProps['as'];
  size?: ContentCardVariants['size'];
  title: string;
  /** A byline under the title, e.g. "by CommBank". */
  description?: string;
  /** The timed line beside the clock, e.g. "16 Minutes" or "4 min read". */
  meta?: string;
  /** The photo; decorative. The `image` slot takes the consumer's own picture instead. */
  imageSrc?: string;
  /** The title's element; a card in a titled row is an h3. */
  titleAs?: string;
  class?: HTMLAttributes['class'];
}>(), {
  as: 'div',
  size: 'row',
  description: undefined,
  meta: undefined,
  imageSrc: undefined,
  titleAs: 'h3',
  class: undefined,
})
</script>

<template>
  <Primitive
    data-slot="content-card"
    :as="as"
    :class="cn(contentCardVariants({ size }), props.class)"
  >
    <Card
      variant="media"
      data-slot="card-media"
      :class="contentCardMediaVariants({ size })"
    >
      <slot name="image">
        <img
          v-if="imageSrc"
          :src="imageSrc"
          alt=""
          loading="lazy"
          class="absolute inset-0 size-full object-cover"
        >
      </slot>
      <slot />
      <div class="absolute inset-x-2 top-2 flex items-start justify-between gap-2">
        <div>
          <slot name="badge" />
        </div>
        <slot name="corner" />
      </div>
    </Card>
    <div class="flex flex-col gap-1">
      <Heading
        v-if="size === 'lead'"
        :as="titleAs"
        size="xl"
        class="line-clamp-2 pb-1"
        data-slot="card-title"
      >
        {{ title }}
      </Heading>
      <Text
        v-else
        :as="titleAs"
        weight="medium"
        class="line-clamp-3"
        data-slot="card-title"
      >
        {{ title }}
      </Text>
      <Text
        v-if="description"
        size="sm"
        class="text-muted-foreground"
        data-slot="card-description"
      >
        {{ description }}
      </Text>
      <Text
        v-if="meta"
        size="xs"
        class="flex items-center gap-1 text-muted-foreground"
      >
        <Icon
          :name="ICONS.clock"
          size="xs"
          class="shrink-0"
        />
        {{ meta }}
      </Text>
      <div
        v-if="$slots.footer"
        class="flex flex-wrap items-center gap-1 pt-0.5"
        data-slot="card-footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </Primitive>
</template>
